import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "../contexts/TranslationContext";
import "./Popup.scss";
import ConfirmPopup from "./ConfirmPopup";
import { deleteUser, updateUser } from "../api/apiServiceFetch";
import { format } from "date-fns";

const Popup = ({ isOpen, onClose, editData, userDetails, onConfirm }) => {
	const { t } = useTranslation();
	const textareaRef = useRef(null);

	const [showPopup, setShowPopup] = useState(false);
	const [userData, setUserData] = useState({
		id: 0,
		userId: "",
		name: "",
		sName: "",
		userName: "",
		sex: 1,
		birthday: "",
		country: "",
		city: "",
		wRole: "",
		profession: "",
		description: "",
		propose: "",
		minAge: 18,
		maxAge: 99,

		mail: "",
		otp: "",
		otpConfirmed: false,
	});

	useEffect(() => {
		if (!isOpen) return;

		setUserData({
			// Canonical Users.Id — what every moderation endpoint expects under
			// the id-only model. `userId` (TG chat id) is kept around only for
			// display; it's null for mobile accounts.
			id: editData?.id ?? 0,
			userId: editData?.userId ?? 0,
			name: editData?.name ?? "",
			sName: editData?.sName ?? "",
			userName: editData?.userName ?? "",
			sex: Number(editData?.sex ?? 1),
			birthday: editData?.birthday ?? "",
			country: userDetails?.country ?? editData?.country ?? "",
			city: userDetails?.city ?? editData?.city ?? "",
			wRole: userDetails?.wRole ?? editData?.wRole,
			profession: userDetails?.profession ?? editData?.profession,
			description: userDetails?.description ?? editData?.description,
			propose: userDetails?.propose ?? editData?.propose,
			minAge: userDetails?.minAge ?? editData?.minAge,
			maxAge: userDetails?.maxAge ?? editData?.maxAge,
			// UserUpdateModeration DTO carries non-nullable Height/FreezeState/
			// IncoState — omitting them would clobber the current row to 0/false.
			height: userDetails?.height ?? editData?.height ?? 0,
			freezeState: userDetails?.freezeState ?? editData?.freezeState ?? false,
			incoState: userDetails?.incoState ?? editData?.incoState ?? false,

			mail: userDetails?.mail ?? editData?.mail ?? "",
			otp: userDetails?.otp ?? editData?.otp ?? "",
			otpConfirmed:
				editData?.otpConfirmed ??
				editData?.OtpConfirmed ??
				userDetails?.otpConfirmed ??
				false,
		});
	}, [isOpen, editData, userDetails]);

	const autoResizeTextarea = () => {
		if (textareaRef.current) {
			textareaRef.current.style.height = "auto";
			textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
		}
	};

	useEffect(() => {
		autoResizeTextarea();
	}, [userData.description]);

	const formatDate = (date) => {
		if (!date) return "";
		try {
			return format(new Date(date), "yyyy-MM-dd");
		} catch {
			return "";
		}
	};

	const handleDeleteClick = () => {
		setShowPopup(true);
	};

	const handleConfirm = async () => {
		await deleteUser(editData.id ?? editData.userId);
		setShowPopup(false);
		onConfirm();
	};

	const handleCancel = () => {
		setShowPopup(false);
	};

	const handleInputChange = (event) => {
		const { name, value, type, checked } = event.target;

		setUserData((prev) => ({
			...prev,
			[name]:
				type === "radio"
					? Number(value)
					: type === "checkbox"
						? checked
						: value,
		}));
	};

	const handleSave = async () => {
		// Backend resolves the user by Users.Id (PK). Under the id-only model
		// the legacy `userData.userId` carries the Telegram chat id and is NULL
		// for mobile accounts — never use it as the DTO key.
		const toInt = (v) => {
			const n = Number(v);
			return Number.isFinite(n) ? n : 0;
		};
		const DATA = {
			UserId: userData.id ?? userData.userId,
			Name: userData.name,
			SName: userData.sName,
			UserName: userData.userName,
			Sex: toInt(userData.sex),
			Birthday: userData.birthday || null,
			Country: userData.country,
			City: userData.city,
			WRole: userData.wRole,
			Profession: userData.profession,
			Description: userData.description,
			Propose: userData.propose,
			MinAge: toInt(userData.minAge),
			MaxAge: toInt(userData.maxAge),
			// Mirror the non-nullable fields on UserUpdateModeration so a save
			// from the Popup doesn't reset height/freeze/incognito server-side.
			Height: toInt(userData.height),
			FreezeState: Boolean(userData.freezeState),
			IncoState: Boolean(userData.incoState),

			Mail: userData.mail,
			Otp: userData.otp,
			OtpConfirmed: Boolean(userData.otpConfirmed),
		};

		await updateUser(DATA);
		onClose();
	};

	const getValue = (field) => {
		const val = userData?.[field];

		if (val !== undefined && val !== null && val !== "") {
			return t(val);
		}

		return t(userDetails?.[field]) ?? "";
	};

	if (!isOpen) return null;

	// Incognito is only effective for PREMIUM accounts: dbo.incoState() returns
	// 0 unless the user has an active UserOrders row with type=1. `type` comes
	// from the list row (UsersAll): -1 free, 1 PREMIUM (тариф Plus снят).
	const isPremium = Number(editData?.type) === 1;

	return (
		<div className="popup-overlay">
			<ConfirmPopup
				isOpen={showPopup}
				onConfirm={handleConfirm}
				onCancel={handleCancel}
			/>

			<div className="popup">
				<h2>{t("Редакт. Профили")}</h2>

				<div className="popup-form-grid">
					<div>
						<label htmlFor="name">Имя</label>
						<input
							id="name"
							type="text"
							name="name"
							value={userData.name}
							onChange={handleInputChange}
						/>
					</div>

					<div>
						<label htmlFor="sName">Фамилия</label>
						<input
							id="sName"
							type="text"
							name="sName"
							value={userData.sName}
							onChange={handleInputChange}
						/>
					</div>

					<div>
						<label htmlFor="mail">Mail</label>
						<input
							id="mail"
							type="email"
							name="mail"
							value={userData.mail}
							onChange={handleInputChange}
						/>
					</div>

					<div>
						<label htmlFor="otp">OTP</label>
						<input
							id="otp"
							type="text"
							name="otp"
							value={userData.otp}
							onChange={handleInputChange}
						/>
					</div>

					<div>
						<label htmlFor="birthday">Дата рождения</label>
						<input
							id="birthday"
							type="date"
							max={formatDate(
								new Date().setFullYear(new Date().getFullYear() - 18),
							)}
							name="birthday"
							value={formatDate(userData.birthday)}
							onChange={handleInputChange}
						/>
					</div>

					<div className="gender">
						<div
							className={`gender-option ${
								Number(userData.sex) === 1 ? "active male" : "male"
							}`}
						>
							<input
								id="male"
								type="radio"
								name="sex"
								value={1}
								checked={Number(userData.sex) === 1}
								onChange={handleInputChange}
							/>
							<label htmlFor="male">Мужчина</label>
						</div>

						<div
							className={`gender-option ${
								Number(userData.sex) === 2 ? "active female" : "female"
							}`}
						>
							<input
								id="female"
								type="radio"
								name="sex"
								value={2}
								checked={Number(userData.sex) === 2}
								onChange={handleInputChange}
							/>
							<label htmlFor="female">Женщина</label>
						</div>
					</div>

					<div>
						<label htmlFor="wRole">Роль</label>
						<input
							id="wRole"
							type="text"
							name="wRole"
							value={getValue("wRole")}
							onChange={handleInputChange}
						/>
					</div>

					<div>
						<label htmlFor="profession">Сфера деятельности</label>
						<input
							id="profession"
							type="text"
							name="profession"
							value={getValue("profession")}
							onChange={handleInputChange}
						/>
					</div>

					<div>
						<label htmlFor="minAge">Возраст поиска: от</label>
						<input
							id="minAge"
							type="number"
							name="minAge"
							min={18}
							max={99}
							value={userData.minAge ?? ""}
							onChange={handleInputChange}
						/>
					</div>

					<div>
						<label htmlFor="maxAge">Возраст поиска: до</label>
						<input
							id="maxAge"
							type="number"
							name="maxAge"
							min={18}
							max={99}
							value={userData.maxAge ?? ""}
							onChange={handleInputChange}
						/>
					</div>

					<div className="span-2 otp-confirmed-row">
						<label htmlFor="otpConfirmed">OTP Confirmed</label>
						<input
							id="otpConfirmed"
							type="checkbox"
							name="otpConfirmed"
							checked={Boolean(userData.otpConfirmed)}
							onChange={handleInputChange}
						/>
					</div>

					<div className="span-2">
						<label htmlFor="description">О себе</label>
						<textarea
							id="description"
							className="textArea"
							name="description"
							value={getValue("description")}
							onChange={handleInputChange}
							maxLength={500}
							placeholder={t("descriptionMAX")}
							ref={textareaRef}
						/>
					</div>

					<div className="span-2 status-block">
						<h3>Статус профиля</h3>

						<div className="status-row">
							<div className="status-info">
								<span className="status-label">Заморозка</span>
								<span
									className={`status-value ${
										userData.freezeState ? "badge-alert" : "badge-on"
									}`}
								>
									{userData.freezeState ? "Заморожен" : "Активен"}
								</span>
							</div>
							<label className="switch">
								<input
									type="checkbox"
									name="freezeState"
									checked={Boolean(userData.freezeState)}
									onChange={handleInputChange}
								/>
								<span className="slider" />
							</label>
						</div>

						<div className="status-row">
							<div className="status-info">
								<span className="status-label">Инкогнито</span>
								<span
									className={`status-value ${
										userData.incoState ? "badge-on" : "badge-off"
									}`}
								>
									{userData.incoState ? "Включено" : "Выключено"}
								</span>
								{!isPremium && (
									<span className="status-note">Только для PREMIUM</span>
								)}
							</div>
							<label
								className={`switch ${!isPremium ? "switch--disabled" : ""}`}
							>
								<input
									type="checkbox"
									name="incoState"
									checked={Boolean(userData.incoState)}
									onChange={handleInputChange}
									disabled={!isPremium}
								/>
								<span className="slider" />
							</label>
						</div>
					</div>
				</div>

				<div className="language-selector-container">
					<button onClick={handleDeleteClick} className="del-button">
						{t("delete")}
					</button>
				</div>

				<div className="popup-buttons">
					<button onClick={handleSave} className="save-button">
						{t("save")}
					</button>
					<button onClick={onClose} className="cancel-button">
						{t("cancel")}
					</button>
				</div>
			</div>
		</div>
	);
};

export default Popup;
